/**
 * types.ts — Shared TypeScript Types for Sophia
 *
 * This is the single source of truth for all data shapes used across:
 * - Chrome Extension (packages/extension)
 * - Backend API (packages/api)
 *
 * All types use strict TypeScript with no `any`.
 * Zod schemas are provided alongside types for runtime validation.
 */

import { z } from 'zod';

// ============================================================
// PRODUCT DATA — Extracted from shopping page DOM
// ============================================================

export const ProductDataSchema = z.object({
  /** Raw product title from the page */
  title: z.string().min(1).max(500),
  /** Current price as a number (not a string) */
  price: z.number().positive(),
  /** ISO 4217 currency code: "AED", "SAR", "USD", etc. */
  currency: z.string().length(3).default('AED'),
  /** Full URL of the product page */
  url: z.string().url(),
  /** Hostname of the retailer: "zara.com", "noon.com", etc. */
  retailerDomain: z.string().min(3).max(100),
  /** Brand name if available from DOM (may be null) */
  brand: z.string().max(100).nullable().optional(),
  /** SKU or product ID from DOM (may be null) */
  sku: z.string().max(100).nullable().optional(),
  /** Primary product image URL (may be null) */
  imageUrl: z.string().url().nullable().optional(),
  /**
   * User's self-reported item condition. Defaults to 'very_good' on the
   * server when omitted. Passed through when the user picks a different
   * condition via the extension's slider.
   */
  condition: z.enum(['mint', 'excellent', 'very_good', 'good', 'fair']).optional(),
});

export type ProductData = z.infer<typeof ProductDataSchema>;

// ============================================================
// NORMALIZED PRODUCT — After AI processing
// ============================================================

export const NormalizedProductSchema = z.object({
  brand: z.string().max(100),
  model: z.string().max(200),
  category: z.string().max(100),
  color: z.string().max(50).nullable(),
  normalizedAt: z.string().datetime(),
});

export type NormalizedProduct = z.infer<typeof NormalizedProductSchema>;

// ============================================================
// ALTERNATIVE PRODUCT — From affiliate networks
// ============================================================

export const AlternativeProductSchema = z.object({
  /** Retailer name (display name): "Noon", "ASOS", "Farfetch" */
  retailerName: z.string().max(100),
  /** Product title on the alternative retailer */
  title: z.string().max(500),
  /** Price on the alternative retailer */
  price: z.number().positive(),
  /** Currency code */
  currency: z.string().length(3),
  /**
   * Affiliate deep link OR Google Shopping offer URL.
   * Base64 data URIs (imageUrl) are also accepted — Serper embeds product
   * thumbnails inline, so the Zod .url() constraint would reject them.
   */
  affiliateUrl: z.string().min(1),
  /** Product image on alternative site (may be null). Accepts http(s) OR data: URIs. */
  imageUrl: z.string().nullable().optional(),
  /** Whether item is in stock */
  inStock: z.boolean().default(true),
  /** Match confidence score 0-1 (how similar this is to source product) */
  matchScore: z.number().min(0).max(1),
  /** Which source this came from */
  source: z.enum(['database', 'cj', 'impact', 'admitad', 'direct', 'scrape', 'serper']),
});

export type AlternativeProduct = z.infer<typeof AlternativeProductSchema>;

// ============================================================
// RESALE ESTIMATE
// ============================================================

export const ResaleEstimateSchema = z.object({
  /** Estimated resale price in same currency as original */
  estimated: z.number().positive(),
  /** Confidence level based on data availability */
  confidence: z.enum(['high', 'medium', 'low']),
  /**
   * Numeric confidence in [0, 1]. Finer-grained than `confidence` so
   * the UI can render a bar/dot intensity. Formula documented in
   * services/resale.ts (weightedComps / targetComps × variantMatchRatio).
   */
  confidenceScore: z.number().min(0).max(1).optional(),
  /** Number of actual sales used to generate estimate */
  sampleSize: z.number().int().min(0),
  /** Human-readable note about the estimate */
  note: z.string().max(200).optional(),
  /**
   * Which method produced this estimate. `market_comparables` means we
   * have real resale listings right now; `brand_retention` uses published
   * brand-level data (Rebag CLAIR / TRR reports); `category_fallback`
   * is the last-resort heuristic.
   */
  method: z.enum(['market_comparables', 'brand_retention', 'category_fallback']).optional(),
  /** Interquartile range of the comparable listings, if available */
  range: z.object({ low: z.number(), high: z.number() }).nullable().optional(),
  /** Retailer display names that contributed to the comparable set */
  sources: z.array(z.string()).optional(),
  /** Human-readable methodology line for the UI/tooltip */
  methodology: z.string().max(300).optional(),
  /**
   * Item condition the estimate was computed for. Defaults to
   * "very_good" (multiplier 1.00) when the user hasn't touched the
   * slider — matches Rebag/TRR default assumption.
   */
  condition: z.enum(['mint', 'excellent', 'very_good', 'good', 'fair']).optional(),
});

export type ResaleEstimate = z.infer<typeof ResaleEstimateSchema>;

// ============================================================
// COMPARISON RESULT — Full API response to extension
// ============================================================

export const AuthenticityWarningSchema = z.object({
  level: z.enum(['caution', 'warning']),
  message: z.string(),
  tips: z.array(z.string()),
});

export type AuthenticityWarning = z.infer<typeof AuthenticityWarningSchema>;

export const ComparisonResultSchema = z.object({
  /** AI-normalized product attributes */
  normalized: NormalizedProductSchema,
  /** Original price from the page */
  currentPrice: z.number().positive(),
  /** Currency code */
  currency: z.string().length(3),
  /** Alternative products sorted by price (cheapest first, max 5) */
  alternatives: z.array(AlternativeProductSchema),
  /** Resale estimate (null if not applicable or not enough data) */
  resaleValue: ResaleEstimateSchema.nullable(),
  /** Potential savings vs cheapest alternative (0 if no alternatives) */
  savings: z.number().min(0),
  /** Authenticity warning if price seems suspiciously low */
  authenticityWarning: AuthenticityWarningSchema.nullable().optional(),
  /** ISO timestamp of when this result was generated or retrieved from cache */
  timestamp: z.string().datetime(),
  /** If result came from cache, this is the cache timestamp */
  cachedAt: z.string().datetime().nullable(),
  /**
   * Honesty signal for the UI:
   *   'live'    — at least one retailer was reached live; prices/URLs are real
   *   'curated' — no live results; falling back to verified seed listings
   *   'none'    — we have nothing confident; UI should offer search links only
   */
  dataSource: z.enum(['live', 'curated', 'none']).optional(),
  /**
   * Honest-state fallback: direct search URLs to hand the user when we
   * can't confirm a better price. Never shown alongside real alternatives.
   */
  searchLinks: z
    .array(z.object({ retailerName: z.string(), url: z.string().url() }))
    .optional(),
});

export type ComparisonResult = z.infer<typeof ComparisonResultSchema>;

// ============================================================
// API RESPONSE ENVELOPE — Standard wrapper for all API responses
// ============================================================

export type ApiResponse<T> =
  | { success: true; data: T }
  | { success: false; error: string; code: string; details?: unknown };

// ============================================================
// CONSENT RECORD — Stored locally in chrome.storage.local
// ============================================================

export const ConsentRecordSchema = z.object({
  accepted: z.boolean(),
  timestamp: z.number().int().positive(),
  /** Bump this version to force re-consent if terms change */
  version: z.string(),
});

export type ConsentRecord = z.infer<typeof ConsentRecordSchema>;

// ============================================================
// ANALYTICS EVENTS — Only anonymized events, no PII
// ============================================================

export const AnalyticsEventTypeSchema = z.enum([
  'button_clicked',
  'comparison_completed',
  'affiliate_link_clicked',
  'modal_dismissed',
  'consent_accepted',
  'consent_rejected',
  'error_occurred',
]);

export type AnalyticsEventType = z.infer<typeof AnalyticsEventTypeSchema>;

// ============================================================
// AUTHENTICITY — Tier 2: multi-photo, brand-aware, code-validated
// ============================================================

/**
 * Photo angles we ask users to capture. The brand-specific protocol
 * decides which subset is required for a given item. `other` is a
 * free-form capture slot.
 */
export const PhotoAngleSchema = z.enum([
  'full',         // whole item, well lit
  'logo',         // brand mark close-up
  'stitching',    // macro of stitching
  'hardware',     // zippers, buckles, rivets close-up
  'label',        // interior leather/metal label
  'serial',       // date code / serial number sticker
  'heat_stamp',   // brand heat stamp (where applicable)
  'date_code',    // printed date/micro code (mobile chip era)
  'caseback',     // watch caseback
  'dial',         // watch dial close-up
  'movement',     // watch movement visible through caseback
  'rehaut',       // Rolex rehaut engraving
  'crown',        // watch crown
  'dust_bag',     // packaging
  'box',          // box close-up
  'receipt',      // purchase receipt
  'papers_and_warranty', // v3: watch papers / warranty card / certificate (Rolex/Patek/AP/Omega)
  'other',
]);

export type PhotoAngle = z.infer<typeof PhotoAngleSchema>;

/**
 * One uploaded photo. `image` may be raw base64 OR a data: URI; the server
 * strips the `data:...;base64,` prefix if present. Size checked server-side.
 */
export const AuthPhotoSchema = z.object({
  angle: PhotoAngleSchema,
  image: z.string().min(100),
  mimeType: z.string().max(60).optional(),
});

export const BrandProtocolKeySchema = z.enum([
  'louis_vuitton',
  'chanel',
  'hermes',
  'rolex',
  'gucci',
  'dior',
  'prada',
  'bottega_veneta',
  'celine',
  'saint_laurent',
  'miu_miu',
  'fendi',
  'loewe',
  'cartier',
  'patek_philippe',
  'audemars_piguet',
  'van_cleef_arpels',
  'bulgari',
  'omega',
  'generic',
]);

export type BrandProtocolKey = z.infer<typeof BrandProtocolKeySchema>;

/**
 * Incoming request. Backward-compatible with the old single-image shape:
 * if `photos` is omitted, the handler wraps `{image, mimeType}` into a
 * single `other`-angle photo and runs generic analysis.
 */
export const AuthenticityRequestSchema = z.object({
  brand: z.string().min(1).max(80).optional(),
  itemType: z.string().max(60).optional(),
  photos: z.array(AuthPhotoSchema).min(1).max(8).optional(),
  code: z.string().max(40).optional(),
  userHint: z.string().max(240).optional(),
  // NEW: cross-signal inputs from the shopping context
  /**
   * Description text from the retailer listing the user was shopping
   * on. Used by the listing-text NLP cross-signal to detect replica-
   * posture language ("1:1 quality", "AAA mirror", "factory direct").
   */
  listingText: z.string().max(4000).optional(),
  /**
   * The listed price shown on the page, in its native currency. Used
   * by the price-anomaly cross-signal: we fetch the market median via
   * Serper and flag if the current price is suspiciously below market.
   */
  currentPrice: z.number().positive().optional(),
  priceCurrency: z.string().length(3).optional(),
  /**
   * Origin retailer domain (e.g. "therealreal.com"). Stored with the
   * scan so we can later surface retailer-level accuracy stats and
   * flag recurring bad sellers.
   */
  retailerDomain: z.string().max(100).optional(),
  /**
   * EXIF metadata extracted client-side from each uploaded photo.
   * Array positions correspond 1:1 with photos[]. Optional per-photo —
   * most of these fields are often absent even in authentic photos,
   * but when present they carry useful anomaly signals.
   */
  photoMetadata: z.array(z.object({
    /** Photo EXIF DateTimeOriginal as ISO string */
    captureDate: z.string().datetime().nullable().optional(),
    /** GPS latitude (decimal degrees) */
    lat: z.number().min(-90).max(90).nullable().optional(),
    /** GPS longitude (decimal degrees) */
    lng: z.number().min(-180).max(180).nullable().optional(),
    /** Camera make + model combined, e.g., "Apple iPhone 15" */
    camera: z.string().max(80).nullable().optional(),
  })).max(8).optional(),
  // Legacy single-photo fields — still accepted
  image: z.string().min(100).optional(),
  mimeType: z.string().max(60).optional(),
  /**
   * Service tier for this authentication request:
   *   - 'free'      Listing-screen path only (no user photo analysis).
   *                 Used by the Chrome extension "Should I Buy?" flow.
   *                 Reads retailer listing text + retailer-trust tier +
   *                 serial-code validator + brand KB signals. No multi-
   *                 model vision ensemble, no reference-image fetch.
   *   - 'standard'  Full multi-model vision ensemble + reference image
   *                 comparison + all cross-signals. Target SLA <60s.
   *                 Paid — $9.99 per item. Homepage photo upload flow.
   *   - 'rush'      Same as standard but explicitly prioritised; skips
   *                 reference image fetch for speed. Target SLA <30s.
   *                 Paid — $14.99 per item.
   *
   * Defaults to 'free' when omitted.
   */
  tier: z.enum(['free', 'standard', 'rush']).optional(),
  /**
   * Payment token for paid tiers (Stripe PaymentIntent id or equivalent).
   * Required when tier is 'standard' or 'rush' UNLESS a fully-waiving
   * promoCode is supplied. Ignored for 'free' tier. Validation of token
   * vs Stripe happens in lib/billing.ts (stub for MVP — accepts any
   * non-empty string; wire to Stripe before launch).
   */
  paymentToken: z.string().max(200).optional(),
  /**
   * Promo / discount code. Validated against promo_codes table and, if
   * the code is a 'full' waiver, bypasses the paymentToken requirement.
   * For 'percent' and 'fixed' discount codes the remainder still needs
   * paymentToken (reduced amount).
   */
  promoCode: z.string().max(40).optional(),
  // ====== v3 intake extensions (Apr 2026) ======
  // Used as priors by the era/country/quality rule engines. Never
  // decisive on their own — always combined with physical evidence.
  /**
   * Claimed year of production/purchase as reported by the user.
   * Feeds the era-rules engine: e.g., a Chanel handbag claimed 2023
   * should have a microchip (not a serial sticker) per era-rules.json.
   */
  claimedYear: z.number().int().min(1900).max(2100).optional(),
  /**
   * Seller-type prior. 'boutique' = brand-direct (lowest risk),
   * 'professional' = authenticated reseller (medium-low),
   * 'individual' = private seller (higher risk — needs stronger
   * corroboration before authentic verdict).
   */
  sellerType: z.enum(['individual', 'professional', 'boutique']).optional(),
  /**
   * Which supporting documents/accessories are physically present.
   * Additive positive signal only — never decisive. Packaging never
   * outweighs physical evidence of the item.
   */
  documentsPresent: z
    .array(z.enum([
      'card',
      'receipt',
      'dust_bag',
      'box',
      'authenticity_microchip',
    ]))
    .max(5)
    .optional(),
  /**
   * Country stamp observed on the item (e.g., "Made in France", "Italy",
   * "Switzerland"). Free-text; normalised by services/auth-country.ts.
   * Feeds the country-mapping truth table (Chanel FR/IT/ES cross-
   * fungible; Hermès FR-only; Loewe ES-only; etc.).
   */
  countryStamp: z.string().max(80).optional(),
  /**
   * v3.7 W2 — per-scan photo storage consent (PDPL Art. 6 — separate
   * consent per processing purpose).
   *
   * Tier 1 (allow_dispute_storage): default ON in the UI. Authorises
   *   90-day retention so the user / partner expert can re-examine
   *   the exact photos if the verdict is contested. Defensible as
   *   "necessary for the service" under PDPL Art. 5(b).
   *
   * Tier 2 (allow_training_use): default OFF, opt-in. Authorises
   *   indefinite retention + use as ML training data. Separate
   *   purpose, requires explicit affirmative consent.
   *
   * Absent / empty object = legacy / pre-v3.7 client. Treated as
   * { allow_dispute_storage: false, allow_training_use: false } —
   * no photo persisted.
   */
  photoStorageConsent: z
    .object({
      allow_dispute_storage: z.boolean(),
      allow_training_use: z.boolean(),
      consent_version: z.number().int().min(1).default(1),
    })
    .optional(),
});

export type AuthenticityRequest = z.infer<typeof AuthenticityRequestSchema>;

export const AuthFindingStatusSchema = z.enum(['pass', 'caution', 'fail']);

export const PerAngleFindingSchema = z.object({
  angle: PhotoAngleSchema,
  status: AuthFindingStatusSchema,
  observation: z.string().max(280),
  /** Relative contribution 0-1 to the aggregated score */
  weight: z.number().min(0).max(1),
});

export const CodeValidationSchema = z.object({
  provided: z.string().max(40),
  format: z.enum(['valid', 'suspicious_format', 'unknown_format']),
  yearInferred: z.number().int().min(1950).max(2100).nullable(),
  reason: z.string().max(200),
});

export const AuthenticityResultSchema = z.object({
  // v3: insufficient_evidence is the preferred abstention value.
  // 'inconclusive' retained for backward compatibility with legacy
  // scans / clients; treat the two as synonyms at the UI layer.
  verdict: z.enum([
    'likely_authentic',
    'insufficient_evidence',
    'inconclusive',
    'likely_counterfeit',
  ]),
  confidence: z.number().min(0).max(100),
  riskLevel: z.enum(['low', 'medium', 'high']),
  summary: z.string().max(320),
  /** Per-photo analysis (empty if only one photo submitted) */
  perAngle: z.array(PerAngleFindingSchema).max(8).default([]),
  /** Aggregated findings across all signals */
  findings: z
    .array(
      z.object({
        area: z.string().max(80),
        status: AuthFindingStatusSchema,
        note: z.string().max(240),
      }),
    )
    .max(12),
  recommendations: z.array(z.string().max(220)).max(6),
  /** Code validation result, when a code was provided */
  codeValidation: CodeValidationSchema.nullable().default(null),
  /**
   * Reference matches from the FashionCLIP visual-similarity index.
   * Top neighbours from the authentic_examples corpus that look
   * visually similar to the user's primary photo. Surfaced so the
   * UI can show "we matched N similar items in our reference index"
   * — the strongest positive signal Sophia has access to.
   * Empty array when retrieval was disabled, no matches above the
   * cutoff, or the worker was unreachable.
   */
  referenceMatches: z
    .array(
      z.object({
        brand: z.string().max(60),
        category: z.string().max(40),
        model: z.string().max(120).nullable(),
        year: z.number().int().nullable(),
        country: z.string().max(40).nullable(),
        groundTruthVerdict: z.string().max(20),
        similarity: z.number().min(0).max(1),
        url: z.string().url().max(400),
      }),
    )
    .max(5)
    .default([]),
  /**
   * Placeholder for the human-in-the-loop escalation pipeline (Phase 3 of
   * the authenticator improvement roadmap, 2026-04-29). When sub-confidence
   * scans on high-value brands are eligible we'll populate `eligible: true`
   * and a `status` of 'queued' | 'in_review' | 'completed'. Today this is
   * always `eligible: false, status: null` so the schema is stable for the
   * upcoming UI work without forcing every existing client to migrate.
   */
  escalation: z
    .object({
      eligible: z.boolean(),
      status: z.enum(['queued', 'in_review', 'completed']).nullable(),
      etaHours: z.number().int().nullable(),
      partnerLabel: z.string().max(60).nullable(),
    })
    .default({ eligible: false, status: null, etaHours: null, partnerLabel: null }),
  /**
   * v3.5 Phase 2G — decision-tier breakdown. Replaces the user-facing
   * "verdict + confidence%" binary with a transparent count of what
   * the model actually saw. Lets the UI render
   *
   *   "5 markers consistent with authentic / 2 concerns / 1 inconclusive
   *    — overall: leaning authentic"
   *
   * instead of "likely_authentic @ 78%". The verdict + confidence are
   * still present for back-compat; this is the new primary surface.
   */
  decisionBreakdown: z
    .object({
      markersTotal: z.number().int().min(0),
      markersWeightSum: z.number().int().min(0),
      concernsTotal: z.number().int().min(0),
      concernsWeightSum: z.number().int().min(0),
      inconclusiveTotal: z.number().int().min(0),
      /** Net evidence balance: markersWeightSum minus concernsWeightSum. Positive = leans authentic. */
      net: z.number().int(),
      leaning: z.enum([
        'strongly_authentic',
        'leaning_authentic',
        'mixed',
        'leaning_counterfeit',
        'strongly_counterfeit',
        'insufficient',
      ]),
      /** Top 3 markers by weight, surfaced for UI rendering. */
      topMarkers: z.array(z.object({ note: z.string().max(160), weight: z.number().int().min(1).max(5) })).max(5).default([]),
      /** Top 3 concerns by weight, surfaced for UI rendering. */
      topConcerns: z.array(z.object({ note: z.string().max(160), weight: z.number().int().min(1).max(5) })).max(5).default([]),
    })
    .default({
      markersTotal: 0,
      markersWeightSum: 0,
      concernsTotal: 0,
      concernsWeightSum: 0,
      inconclusiveTotal: 0,
      net: 0,
      leaning: 'insufficient',
      topMarkers: [],
      topConcerns: [],
    }),
  /** Which protocol was applied */
  protocol: BrandProtocolKeySchema,
  /** Which model (or ensemble label) produced the core assessment */
  model: z.string().max(80),
  /** Timestamp of analysis */
  timestamp: z.string().datetime(),
  /**
   * DB id of the logged scan. Pass this to /api/authenticity/feedback
   * to attach user feedback. Null when the DB layer was unreachable —
   * the verdict is still valid, we just couldn't persist this scan.
   */
  scanId: z.string().uuid().nullable().optional(),
});

// ============================================================
// AUTHENTICITY FEEDBACK — user "was this right?" signal
// ============================================================

export const AuthenticityFeedbackSchema = z.object({
  scanId: z.string().uuid(),
  rating: z.enum(['helpful', 'not_helpful', 'flagged_wrong', 'flagged_fake']),
  notes: z.string().max(500).nullable().optional(),
  groundTruth: z.enum(['authentic', 'counterfeit', 'unknown']).nullable().optional(),
});

export type AuthenticityFeedback = z.infer<typeof AuthenticityFeedbackSchema>;

export type AuthenticityResult = z.infer<typeof AuthenticityResultSchema>;

// Backward-compat alias so existing mobile/UI code doesn't break.
export const AuthenticityCheckSchema = AuthenticityResultSchema;
export type AuthenticityCheck = AuthenticityResult;

// ============================================================
// UI-SAFE BRAND PROTOCOLS
// The full server-side protocol (with AI guidance strings) lives in
// packages/api/services/auth-protocols.ts. This is the subset the
// landing page and mobile app need to render the capture wizard.
// ============================================================

export interface UiAngle {
  angle: PhotoAngle;
  required: boolean;
  label: { en: string; ar: string };
  instruction: { en: string; ar: string };
}

export interface UiBrandProtocol {
  key: BrandProtocolKey;
  displayName: string;
  itemTypes: string[];
  angles: UiAngle[];
  codeFormat?: {
    label: { en: string; ar: string };
    example: string;
    note: { en: string; ar: string };
  };
}

export const UI_BRAND_PROTOCOLS: UiBrandProtocol[] = [
  {
    key: 'louis_vuitton',
    displayName: 'Louis Vuitton',
    itemTypes: ['Handbag', 'Small leather goods', 'Luggage', 'Shoes', 'Accessory'],
    angles: [
      { angle: 'full',      required: true,  label: { en: 'Full item',              ar: 'القطعة كاملة' },  instruction: { en: 'Straight-on photo of the whole piece, even lighting, plain surface.',                 ar: 'صورة من الأمام للقطعة كاملة على سطح مستوٍ وإضاءة متساوية.' } },
      { angle: 'logo',      required: true,  label: { en: 'Heat stamp close-up',    ar: 'ختم الشعار من قريب' },  instruction: { en: 'Macro shot of the "LOUIS VUITTON" heat stamp.',                                          ar: 'صورة قريبة جدًا لختم "LOUIS VUITTON" الداخلي أو الخارجي.' } },
      { angle: 'stitching', required: true,  label: { en: 'Stitching macro',        ar: 'صورة الخياطة من قريب' },  instruction: { en: 'Close shot of a stitched edge — at least 10 stitches visible.',                        ar: 'صورة مكبّرة لحافة مخيطة بحيث تظهر 10 غرز على الأقل.' } },
      { angle: 'hardware',  required: true,  label: { en: 'Hardware close-up',      ar: 'صورة القطعة المعدنية من قريب' },  instruction: { en: 'Close-up of zipper pull, buckle, lock, or D-ring.',                                      ar: 'صورة قريبة للسحاب أو الإبزيم أو القفل أو الحلقة.' } },
      { angle: 'date_code', required: false, label: { en: 'Date code / chip area',  ar: 'ختم التاريخ أو الرقاقة' },  instruction: { en: 'Pre-2021: interior pocket stamp. 2021+: interior label area (microchip).',              ar: 'قبل 2021: ختم داخل الجيب. بعد 2021: منطقة الرقاقة الداخلية.' } },
      { angle: 'label',     required: false, label: { en: 'Interior lining',        ar: 'البطانة الداخلية' },  instruction: { en: 'Open the bag and photograph the interior lining.',                                      ar: 'افتح الحقيبة وصوّر البطانة الداخلية.' } },
      { angle: 'dust_bag',  required: false, label: { en: 'Dust bag',               ar: 'كيس القماش' },      instruction: { en: 'If available, photograph the dust bag.',                                                ar: 'إذا كان متاحًا صوّر كيس القماش والشعار.' } },
      { angle: 'receipt',   required: false, label: { en: 'Receipt / Certificate',  ar: 'الإيصال أو الشهادة' },  instruction: { en: 'If you have the original purchase receipt, photograph it. Blur personal details.',     ar: 'إذا كان لديك إيصال الشراء الأصلي صوّره. يمكنك إخفاء البيانات الشخصية.' } },
    ],
    codeFormat: {
      label:   { en: 'LV Date Code (pre-2021)',                                                                          ar: 'رمز تاريخ LV (قبل 2021)' },
      example: 'e.g. FL1152',
      note:    { en: '2 letters (factory) + 4 digits. Skip if your item is 2021 or newer.',                               ar: 'حرفان (المصنع) + 4 أرقام. اتركه فارغًا للقطع بعد 2021.' },
    },
  },
  {
    key: 'chanel',
    displayName: 'Chanel',
    itemTypes: ['Handbag', 'Small leather goods', 'Shoes', 'Accessory'],
    angles: [
      { angle: 'full',      required: true,  label: { en: 'Full item',             ar: 'القطعة كاملة' },         instruction: { en: 'Photograph the entire piece head-on.',                                          ar: 'صوّر القطعة من الأمام.' } },
      { angle: 'logo',      required: true,  label: { en: 'CC logo / turnlock',    ar: 'شعار CC / القفل' },       instruction: { en: 'Close-up of the CC interlocking logo.',                                         ar: 'صورة قريبة لشعار CC.' } },
      { angle: 'serial',    required: true,  label: { en: 'Hologram serial',       ar: 'ملصق الرقم التسلسلي' },  instruction: { en: 'Macro shot of the interior serial hologram.',                                   ar: 'صورة مكبّرة لملصق الرقم التسلسلي الداخلي.' } },
      { angle: 'stitching', required: true,  label: { en: 'Quilting macro',        ar: 'صورة الخياطة' },         instruction: { en: 'Zoom into one quilting diamond.',                                              ar: 'صورة مكبّرة لمعين واحد.' } },
      { angle: 'hardware',  required: true,  label: { en: 'Chain / hardware',      ar: 'السلسلة أو المعدن' },    instruction: { en: 'Close shot of chain link or turnlock screws.',                                 ar: 'صورة قريبة لسلسلة أو براغي القفل.' } },
      { angle: 'label',     required: false, label: { en: 'Interior stamp',        ar: 'الختم الداخلي' },        instruction: { en: 'Photograph the gold foil interior stamp.',                                     ar: 'صوّر الختم الذهبي الداخلي.' } },
      { angle: 'receipt',   required: false, label: { en: 'Authenticity card',     ar: 'بطاقة الأصالة' },        instruction: { en: 'If available, photograph the authenticity card.',                              ar: 'إذا كان متاحًا صوّر بطاقة الأصالة.' } },
      { angle: 'dust_bag',  required: false, label: { en: 'Dust bag',              ar: 'كيس القماش' },           instruction: { en: 'Photograph the dust bag with its ribbon.',                                     ar: 'صوّر كيس القماش وشريطه.' } },
    ],
    codeFormat: {
      label:   { en: 'Chanel Serial (from hologram)', ar: 'الرقم التسلسلي لشانيل' },
      example: 'e.g. 29876543',
      note:    { en: '8 digits (7 on pre-1986 pieces).', ar: '٨ أرقام (٧ للقطع قبل ١٩٨٦).' },
    },
  },
  {
    key: 'hermes',
    displayName: 'Hermès',
    itemTypes: ['Handbag', 'Small leather goods', 'Scarf', 'Accessory'],
    angles: [
      { angle: 'full',      required: true,  label: { en: 'Full bag, both sides', ar: 'الحقيبة كاملة من الجانبين' },  instruction: { en: 'Front and side photo — check for perfect symmetry.',                       ar: 'الأمام والجانب. تحقق من التماثل الكامل.' } },
      { angle: 'logo',      required: true,  label: { en: 'HERMÈS stamp',          ar: 'ختم هيرميس' },            instruction: { en: 'Macro of the "HERMÈS PARIS" stamp on the front.',                            ar: 'صورة مكبّرة لختم "HERMÈS PARIS" الأمامي.' } },
      { angle: 'stitching', required: true,  label: { en: 'Saddle stitch macro',   ar: 'صورة الخياطة' },          instruction: { en: 'Very close shot of the saddle stitch on top edge.',                          ar: 'صورة مكبّرة جدًا لخياطة السرج.' } },
      { angle: 'hardware',  required: true,  label: { en: 'Turnlock hardware',     ar: 'قفل المفتاح' },            instruction: { en: 'Close-up of turnlock, front and back.',                                       ar: 'صورة قريبة لقفل المفتاح من الأمام والخلف.' } },
      { angle: 'serial',    required: true,  label: { en: 'Blind stamp',           ar: 'الختم الأعمى' },          instruction: { en: 'Find the blind stamp (under front flap or on handle base).',                 ar: 'ابحث عن الختم الأعمى (تحت الطرف الأمامي أو داخل قاعدة المقبض).' } },
      { angle: 'label',     required: false, label: { en: 'Interior leather',      ar: 'الجلد الداخلي' },         instruction: { en: 'Photograph the interior leather.',                                            ar: 'صوّر الجلد الداخلي.' } },
      { angle: 'receipt',   required: false, label: { en: 'Box / Receipt / CITES', ar: 'الصندوق أو الإيصال' },     instruction: { en: 'If available, orange box / receipt / CITES (exotic leathers).',              ar: 'إذا كان متاحًا الصندوق أو الإيصال أو شهادة CITES.' } },
    ],
    codeFormat: {
      label:   { en: 'Blind Stamp',       ar: 'الختم الأعمى' },
      example: 'e.g. Y, Z, A (in square)',
      note:    { en: 'Single letter A-Z with optional frame shape.', ar: 'حرف واحد مع إطار اختياري.' },
    },
  },
  {
    key: 'rolex',
    displayName: 'Rolex',
    itemTypes: ['Watch'],
    angles: [
      { angle: 'full',     required: true,  label: { en: 'Full watch',             ar: 'الساعة كاملة' },         instruction: { en: 'Photograph the entire watch including bracelet.',                           ar: 'صوّر الساعة كاملة بما في ذلك السوار.' } },
      { angle: 'dial',     required: true,  label: { en: 'Dial close-up',          ar: 'قرص الساعة' },            instruction: { en: 'Head-on macro of the dial.',                                                 ar: 'صورة مباشرة مكبّرة للقرص.' } },
      { angle: 'rehaut',   required: true,  label: { en: 'Rehaut engraving',       ar: 'نقش الحافة الداخلية' },  instruction: { en: 'Angle the watch to show the inner ring (rehaut).',                            ar: 'أمل الساعة لتصوير الحلقة الداخلية.' } },
      { angle: 'crown',    required: true,  label: { en: 'Crown + date window',    ar: 'التاج ونافذة التاريخ' },   instruction: { en: 'Close-up of the crown and date window.',                                    ar: 'صورة قريبة للتاج ونافذة التاريخ.' } },
      { angle: 'caseback', required: true,  label: { en: 'Caseback',               ar: 'الظهر' },                instruction: { en: 'Photograph the back of the watch.',                                         ar: 'صوّر ظهر الساعة.' } },
      { angle: 'serial',   required: true,  label: { en: 'Serial number',          ar: 'الرقم التسلسلي' },        instruction: { en: 'Between lugs (older) or rehaut at 6 o\'clock (2008+).',                    ar: 'بين القوائم للموديلات القديمة أو الحافة عند ٦ للحديثة.' } },
      { angle: 'movement', required: false, label: { en: 'Movement (if visible)',  ar: 'الحركة إن كانت ظاهرة' },  instruction: { en: 'Only if caseback is transparent.',                                          ar: 'فقط إذا كان الظهر شفافًا.' } },
    ],
    codeFormat: {
      label:   { en: 'Serial number',  ar: 'الرقم التسلسلي' },
      example: 'e.g. 1234567 or F123456',
      note:    { en: '6 digits (pre-2010) OR letter + 6 digits (2010+).', ar: '٦ أرقام (قبل 2010) أو حرف + ٦ أرقام.' },
    },
  },
  {
    key: 'gucci',
    displayName: 'Gucci',
    itemTypes: ['Handbag', 'Small leather goods', 'Shoes', 'Belt', 'Accessory'],
    angles: [
      { angle: 'full',      required: true,  label: { en: 'Full item',           ar: 'القطعة كاملة' },         instruction: { en: 'Photograph the entire piece head-on.',                                    ar: 'صوّر القطعة كاملة من الأمام.' } },
      { angle: 'logo',      required: true,  label: { en: 'Logo close-up',       ar: 'الشعار' },                instruction: { en: 'Macro of the "GUCCI" engraved metal or front logo.',                      ar: 'صورة مكبّرة لأي نقش معدني أو شعار أمامي.' } },
      { angle: 'serial',    required: true,  label: { en: 'Interior serial tag', ar: 'البطاقة الداخلية' },      instruction: { en: 'BOTH SIDES of the interior leather tab.',                                  ar: 'كلا الجانبين من البطاقة الجلدية الداخلية.' } },
      { angle: 'stitching', required: true,  label: { en: 'Stitching macro',     ar: 'الخياطة من قريب' },       instruction: { en: 'Close-up of a stitched edge.',                                            ar: 'صورة قريبة لحافة مخيطة.' } },
      { angle: 'hardware',  required: true,  label: { en: 'Hardware close-up',   ar: 'المعدن من قريب' },        instruction: { en: 'Close shot of zippers, buckle, or chain.',                                ar: 'صورة قريبة للسحاب أو الإبزيم أو السلسلة.' } },
      { angle: 'label',     required: false, label: { en: 'Interior lining',     ar: 'البطانة الداخلية' },      instruction: { en: 'Photograph the interior lining fabric.',                                  ar: 'صوّر نسيج البطانة الداخلية.' } },
      { angle: 'dust_bag',  required: false, label: { en: 'Dust bag',            ar: 'كيس القماش' },           instruction: { en: 'If available, photograph the dust bag.',                                  ar: 'إذا كان متاحًا صوّر كيس القماش.' } },
    ],
    codeFormat: {
      label:   { en: 'Style + Model Code', ar: 'كود الموديل' },
      example: 'e.g. 443497 530916',
      note:    { en: '6 digits style + 4 digits model, from interior leather tag.', ar: '٦ أرقام للطراز + ٤ للموديل.' },
    },
  },
  {
    key: 'generic',
    displayName: 'Other luxury brand',
    itemTypes: ['Handbag', 'Watch', 'Shoes', 'Jewelry', 'Accessory'],
    angles: [
      { angle: 'full',      required: true,  label: { en: 'Full item',         ar: 'القطعة كاملة' },       instruction: { en: 'Entire piece, even lighting, plain background.',                               ar: 'القطعة كاملة بإضاءة متساوية وخلفية بسيطة.' } },
      { angle: 'logo',      required: true,  label: { en: 'Brand logo',        ar: 'شعار الماركة' },       instruction: { en: 'Macro of the logo — interior stamp or exterior tag.',                          ar: 'صورة مكبّرة للشعار.' } },
      { angle: 'stitching', required: true,  label: { en: 'Stitching macro',   ar: 'الخياطة من قريب' },     instruction: { en: 'Close shot of a stitched edge.',                                              ar: 'صورة قريبة لحافة مخيطة.' } },
      { angle: 'hardware',  required: true,  label: { en: 'Hardware',          ar: 'المعدن' },             instruction: { en: 'Close-up of any metal element.',                                              ar: 'صورة قريبة لأي قطعة معدنية.' } },
      { angle: 'label',     required: false, label: { en: 'Interior / Label',  ar: 'الداخل أو الملصق' },   instruction: { en: 'Photograph the interior label or lining.',                                    ar: 'صوّر الملصق الداخلي أو البطانة.' } },
    ],
  },
];

export function getUiProtocol(key: BrandProtocolKey): UiBrandProtocol {
  return UI_BRAND_PROTOCOLS.find((p) => p.key === key) ?? UI_BRAND_PROTOCOLS[UI_BRAND_PROTOCOLS.length - 1]!;
}

export const AnalyticsEventSchema = z.object({
  eventType: AnalyticsEventTypeSchema,
  /** Rotating session UUID — NOT a user ID */
  sessionId: z.string().uuid(),
  metadata: z.record(z.union([z.string(), z.number(), z.boolean()])).optional(),
  timestamp: z.string().datetime(),
});

export type AnalyticsEvent = z.infer<typeof AnalyticsEventSchema>;

// ============================================================
// SUPPORTED RETAILERS — Whitelist must match manifest.json
// ============================================================

export const SUPPORTED_RETAILERS = [
  // GCC luxury resale
  'theluxurycloset.com',
  'bazaara.com',
  'bestkeptshared.com',
  'gemaee.com',
  'dwisi.ae',
  'loveluxury.ae',
  'theclosetonlineshop.com',
  'garderobe.ae',
  'alluuae.com',
  'reemscloset.com',
  're-lovedluxury.com',
  'luxbrandsuae.com',
  'lasuite.ae',
  'shopretold.com',
  'taggy.ae',
  'amusedco.com',
  'luxurypromise.com',
  'shaikhascloset.com',
  'blingblingsabaya.com',
  'libascollective.com',
  'bagsloverdubai.com',
  'ounass.ae',
  'ounass.com',
  'bloomingdales.ae',
  'theluxuryflavor.com',
  'thedealoutlet.com',
  'coach.ae',
  // Global luxury resale
  'vestiairecollective.com',
  'therealreal.com',
  'rebag.com',
  'fashionphile.com',
  'collectorsquare.com',
  'collectorscage.com',
  'thebrandcollector.com',
  'yoogiscloset.com',
  '1stdibs.com',
  // International luxury e-commerce
  'farfetch.com',
  'net-a-porter.com',
  'mytheresa.com',
  'ssense.com',
  '24s.com',
  'modaoperandi.com',
  'saks.com',
  'harrods.com',
  'matchesfashion.com',
  // Brand-direct luxury sites
  'gucci.com',
  'louisvuitton.com',
  'chanel.com',
  'hermes.com',
  'rolex.com',
  'cartier.com',
  'dior.com',
  'prada.com',
  'bottegaveneta.com',
  'celine.com',
  'fendi.com',
  'burberry.com',
  'valentino.com',
  // Watch specialists
  'chrono24.com',
  'watchfinder.com',
  'bobswatches.com',
] as const;

export type SupportedRetailer = typeof SUPPORTED_RETAILERS[number];

// ============================================================
// TRANSLATION KEYS — i18n foundation (English + Arabic)
// ============================================================

export interface TranslationKeys {
  'button.shouldIBuy': string;
  'button.checking': string;
  'modal.title': string;
  'modal.currentPrice': string;
  'modal.savings': string;
  'modal.resaleValue': string;
  'modal.buyNow': string;
  'modal.noAlternatives': string;
  'modal.close': string;
  'consent.title': string;
  'consent.accept': string;
  'consent.reject': string;
  'error.networkError': string;
  'error.timeout': string;
  'error.tryAgain': string;
}
